import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Filter as FilterIcon } from 'lucide-react';
import SavedFilterTabs from '../../../shared/SavedFilterTabs';
import FilterDrawer from '../../../shared/FilterDrawer';
import ColumnPicker from '../../../shared/ColumnPicker';
import useTabColumns from '../../../shared/useTabColumns';
import ReportOverlay from '../../../shared/ReportOverlay';
import DocumentTimeline from '../../../shared/DocumentTimeline';
import Accordion from '../../../shared/Accordion';
import { reportButtonStyle } from '../../../shared/ReportShell';
import { Pagination, PAGE_SIZE_OPTIONS, FALLBACK_PAGE_SIZE, useDefaultPageSize, getCachedDefaultPageSize } from '../../../shared/pagination';
import '../../../shared/StyledInput.css';
import { formatDateTime } from '../../../shared/tenantClock';
import DateInput from '../../../shared/DateInput';
import CancelButton from '../../../shared/CancelButton';

const FILTER_SCREEN_KEY = 'document-milestones';
const DOCUMENT_TYPE_LABELS = { quote: 'Quote', sales_order: 'Sales Order' };
const DOCUMENT_TYPE_FILTER_OPTIONS = [
  { value: 'all', label: 'All document types' },
  { value: 'quote', label: 'Quotes' },
  { value: 'sales_order', label: 'Sales Orders' },
];
// Draft is deliberately not a search dimension (every document has it — not
// useful to filter by) but is still a real recorded milestone, so it does
// show up in a document's own drill-in history below. A Purchase Order entry drops in here the same way once that document type
// ships.
const MILESTONE_OPTIONS = {
  quote: [
    { value: 'all', label: 'All' },
    { value: 'sent', label: 'Sent' },
    { value: 'accepted', label: 'Accepted' },
    { value: 'withdrawn', label: 'Withdrawn' },
    { value: 'converted', label: 'Converted' },
    { value: 'copied_to', label: 'Copied To' },
    { value: 'copied_from', label: 'Copied From' },
  ],
  sales_order: [
    { value: 'all', label: 'All' },
    { value: 'submitted', label: 'Submitted' },
    { value: 'processed', label: 'Accepted' },
    { value: 'unprocessed', label: 'Acceptance Withdrawn' },
    { value: 'revised', label: 'Revised' },
    { value: 'picked', label: 'Picked' },
    { value: 'packed', label: 'Packed' },
    { value: 'shipped', label: 'Shipped' },
    { value: 'converted', label: 'Converted from Quote' },
    { value: 'on_hold', label: 'On Hold' },
  ],
};
const MILESTONE_LABELS = {
  draft: 'Draft', sent: 'Sent', accepted: 'Accepted', withdrawn: 'Withdrawn', converted: 'Converted',
  submitted: 'Submitted', portal_edit: 'Taken Back to Edit', notify_failed: 'New Order Email Not Sent', processed: 'Accepted', unprocessed: 'Acceptance Withdrawn', revised: 'Revised', picked: 'Picked', packed: 'Packed', shipped: 'Shipped',
  on_hold: 'On Hold', off_hold: 'Hold Released', copied_to: 'Copied To', copied_from: 'Copied From',
};
// Shipped/On Hold/Off Hold/Submitted/Accepted/Acceptance Withdrawn can each
// happen more than once over a
// document's life (a repeatable partial-shipment or hold/release
// history — see documentMilestoneService.recordMilestone's `once: false`
// usage) — they don't fit the fixed-order stepper below, which assumes one
// row per milestone name. Shown separately as a plain chronological
// activity log instead, each with its own comment when it has one.
// 'processed' / 'unprocessed' are the stored keys for a Sales Order's
// Accepted / Acceptance Withdrawn, kept from when the step was called Process.
const REPEATABLE_MILESTONES = new Set(['shipped', 'on_hold', 'off_hold', 'submitted', 'portal_edit', 'notify_failed', 'processed', 'unprocessed', 'revised', 'copied_to']);
// Fixed display order per document type — NOT derived from occurred_at.
// Draft and Converted (Sales Order side) are recorded at the exact same
// instant (conversion creates the order already in Draft), so sorting by
// timestamp has no real tiebreak and produces an arbitrary order. A fixed
// order sidesteps that entirely; it also means Picked/Packed always
// display in the right order even when "combine pick and pack" causes
// both to be recorded from the same scan. Mirrors QuoteTimeline.js's own
// recorded order and SalesOrderTimeline.js's step sequence — this table just
// makes it reusable outside a page that already has full quoteMeta/
// orderMeta context.
const MILESTONE_ORDER = {
  quote: ['draft', 'copied_from', 'sent', 'withdrawn', 'accepted', 'converted'],
  sales_order: ['converted', 'draft', 'picked', 'packed'],
};
function milestoneOptionsFor(documentType) {
  if (documentType === 'all') {
    const seen = new Map([['all', { value: 'all', label: 'All' }]]);
    Object.values(MILESTONE_OPTIONS).flat().forEach((m) => { if (m.value !== 'all') seen.set(m.value, m); });
    return [...seen.values()];
  }
  return MILESTONE_OPTIONS[documentType] || [{ value: 'all', label: 'All' }];
}

const OPTIONAL_COLUMNS = [
  { key: 'document_type', label: 'Type' },
  { key: 'customer_name', label: 'Customer' },
];
const SYSTEM_COLUMNS = ['Document #', 'Milestones'];
const DEFAULT_VISIBLE_COLUMNS = OPTIONAL_COLUMNS.map((c) => c.key);

const DEFAULT_FILTERS = { document_type: 'all', milestone: 'all', occurred_from: '', occurred_to: '' };
const FIELD_LABEL_STYLE = { fontSize: 12, color: '#5a6b85' };
const FIELD_INPUT_STYLE = { padding: '6px 10px', borderRadius: 6, border: '1px solid #bfc8d6', fontSize: 14, width: '100%' };

// Drill-in for one row: the report's own row only carries a single
// milestone (whichever one matched the search), so this re-fetches that
// document's FULL history — every milestone it's reached, not just the one
// that matched — and renders it through the same shared/DocumentTimeline.js
// stepper the document's own page uses, plus a real link to open it. Every
// entry here is necessarily reached (there's no "Not yet" placeholder the
// way QuoteTimeline.js/SalesOrderTimeline.js can show one, since building
// that requires knowing the full domain step order/current status, which
// this report doesn't have — only the recorded milestones themselves).
function MilestoneHistoryOverlay({ row, onClose }) {
  const [milestones, setMilestones] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get('/api/document-milestones', { params: { document_type: row.document_type, document_id: row.document_id } })
      .then((res) => setMilestones(res.data.data || []))
      .catch(() => setMilestones([]))
      .finally(() => setLoading(false));
  }, [row.document_type, row.document_id]);

  const order = MILESTONE_ORDER[row.document_type] || [];
  let visible = milestones.filter((m) => order.includes(m.milestone));
  // Converted (Sales Order side) already communicates "this order came
  // into being via conversion" — a separate Draft step for the exact same
  // moment is redundant, not additional information. Quote's own Draft is
  // never suppressed: on that side Draft and Converted are genuinely far
  // apart in time, not a collision.
  if (row.document_type === 'sales_order' && visible.some((m) => m.milestone === 'converted')) {
    visible = visible.filter((m) => m.milestone !== 'draft');
  }
  const sorted = [...visible].sort((a, b) => order.indexOf(a.milestone) - order.indexOf(b.milestone));
  const steps = sorted.map((m, idx) => ({
    key: m.milestone,
    label: MILESTONE_LABELS[m.milestone] || m.milestone,
    reached: true,
    isCurrent: idx === sorted.length - 1,
    date: m.occurred_at,
    comment: m.comment,
  }));

  const activity = milestones
    .filter((m) => REPEATABLE_MILESTONES.has(m.milestone))
    .sort((a, b) => new Date(a.occurred_at || 0) - new Date(b.occurred_at || 0));

  return (
    <ReportOverlay onClose={onClose}>
      <div style={{ background: '#fff', borderRadius: 8, padding: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, marginBottom: 4 }}>
          <div>
            <h3 style={{ margin: '0 0 4px', fontSize: 18 }}>{row.display_number}</h3>
            <div style={{ fontSize: 13, color: '#666' }}>{row.customer_name}</div>
          </div>
          <button type="button" onClick={onClose} style={reportButtonStyle(false)}>Close</button>
        </div>
        <Link to={row.link} style={{ display: 'inline-block', margin: '8px 0 16px', color: '#1976d2', textDecoration: 'underline', fontSize: 13 }}>
          View {DOCUMENT_TYPE_LABELS[row.document_type] || 'document'} →
        </Link>
        {loading ? (
          <div style={{ color: '#888', fontSize: 14 }}>Loading…</div>
        ) : (
          <>
            <DocumentTimeline steps={steps} />
            {activity.length > 0 && (
              <div style={{ marginTop: 20 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#8a94a6', textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 10 }}>Activity</div>
                {activity.map((m, idx) => (
                  <div key={idx} style={{ fontSize: 13, marginBottom: 10, paddingBottom: 10, borderBottom: idx < activity.length - 1 ? '1px solid #eee' : 'none' }}>
                    <div style={{ fontWeight: 600 }}>{MILESTONE_LABELS[m.milestone] || m.milestone}</div>
                    <div style={{ color: '#888', fontSize: 12 }}>{m.occurred_at ? formatDateTime(m.occurred_at) : 'Date unknown'}</div>
                    {m.comment && <div style={{ color: '#333', marginTop: 2 }}>{m.comment}</div>}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </ReportOverlay>
  );
}

// Answers "when was that Quote approved" / "when was that Sales Order
// fully shipped" across every document of a type at once, rather than
// opening one document at a time to read its own Timeline sidebar.
// Reads document_milestones (backend/models/documentMilestone.js) via
// GET /api/document-milestones/search, which groups matches by document
// (one Accordion per document, paginated by document — see the
// controller's own comment) rather than one row per milestone event, so a
// document with several matching milestones stays together instead of
// scattering across pages. Expanding a section lists just the milestones
// that matched the current filters; "Full history" opens
// MilestoneHistoryOverlay above for that document's complete timeline
// (including milestones outside the filter) plus a real link to open it.
// Both an always-visible Filters button (ad hoc, unsaved) and
// SavedFilterTabs (default + your own saved tabs) share the same filter
// fields, matching SalesOrdersTable.js's dual pattern.
function DocumentMilestonesPage() {
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(DEFAULT_VISIBLE_COLUMNS);
  const tabColumns = useTabColumns(OPTIONAL_COLUMNS, SYSTEM_COLUMNS);
  const showType = visibleColumns.includes('document_type');
  const showCustomer = visibleColumns.includes('customer_name');
  const [filterDraft, setFilterDraft] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const activeFilterCount = Object.keys(DEFAULT_FILTERS).filter((k) => String(filters[k] || '') !== String(DEFAULT_FILTERS[k] || '')).length;
  // Free-text, unbounded — deliberately an ad hoc search box rather than a
  // SavedFilterTabs dimension (same split Pricelists/Products/Quotes use
  // for their own free-text search — see QuotesTable.js's po search).
  // Matches against a document's display number or customer name.
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(() => getCachedDefaultPageSize() || FALLBACK_PAGE_SIZE);
  const [pagination, setPagination] = useState({ page: 1, limit, total: 0, pages: 1 });

  useDefaultPageSize(setLimit);

  useEffect(() => {
    setLoading(true);
    axios.get('/api/document-milestones/search', {
      params: {
        document_type: filters.document_type,
        milestone: filters.milestone,
        occurred_from: filters.occurred_from || undefined,
        occurred_to: filters.occurred_to || undefined,
        search: search || undefined,
        page,
        limit,
      },
    })
      .then((res) => {
        setRows(res.data.data || []);
        setPagination(res.data.pagination || { page, limit, total: 0, pages: 1 });
      })
      .catch(() => {
        setRows([]);
        setPagination({ page, limit, total: 0, pages: 1 });
      })
      .finally(() => setLoading(false));
  }, [filters, search, page, limit]);

  const applyFilters = (values) => { setFilters(values); setPage(1); };
  const openFilterPanel = () => setFilterDraft({ ...filters });
  const closeFilterPanel = () => setFilterDraft(null);
  const setFilterDraftField = (key, value) => setFilterDraft((prev) => ({ ...prev, [key]: value }));
  const applyFilterPanel = () => { applyFilters(filterDraft); closeFilterPanel(); };
  const clearFilterPanel = () => { applyFilters(DEFAULT_FILTERS); closeFilterPanel(); };

  const renderDrawerFields = (draft, setDraftField) => (
    <>
      <div>
        <label style={FIELD_LABEL_STYLE}>Document type</label><br />
        <select
          className="styled-input"
          value={draft.document_type}
          onChange={(e) => { setDraftField('document_type', e.target.value); setDraftField('milestone', 'all'); }}
          style={FIELD_INPUT_STYLE}
        >
          {DOCUMENT_TYPE_FILTER_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>
      <div>
        <label style={FIELD_LABEL_STYLE}>Milestone</label><br />
        <select className="styled-input" value={draft.milestone} onChange={(e) => setDraftField('milestone', e.target.value)} style={FIELD_INPUT_STYLE}>
          {milestoneOptionsFor(draft.document_type).map((m) => (
            <option key={m.value} value={m.value}>{m.label}</option>
          ))}
        </select>
      </div>
      <div>
        <label style={FIELD_LABEL_STYLE}>From</label><br />
        <DateInput className="styled-input" value={draft.occurred_from} onChange={(e) => setDraftField('occurred_from', e.target.value)} style={FIELD_INPUT_STYLE} />
      </div>
      <div>
        <label style={FIELD_LABEL_STYLE}>To</label><br />
        <DateInput className="styled-input" value={draft.occurred_to} onChange={(e) => setDraftField('occurred_to', e.target.value)} style={FIELD_INPUT_STYLE} />
      </div>
    </>
  );

  return (
    <div style={{ margin: '32px 24px 48px', background: '#fafafa', border: '1px solid #eceef1', borderRadius: 12, boxShadow: '0 1px 6px rgba(0,0,0,0.04)', paddingBottom: 32 }}>
      <div style={{ padding: '20px 24px 0' }}>
        <h2 style={{ margin: 0, fontWeight: 600, fontSize: 20 }}>Document Milestones</h2>
      </div>

      <div style={{ display: 'flex', gap: 10, margin: '16px 24px 0', alignItems: 'center', flexWrap: 'wrap' }}>
        <div>
          <input
            className="styled-input"
            placeholder="Search document # or customer..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            style={{ width: 240 }}
          />
          {search && (
            <button type="button" onClick={() => { setSearch(''); setPage(1); }} title="Clear search" style={{ marginLeft: 6, padding: '4px 8px', fontSize: 12 }}>×</button>
          )}
        </div>
        <button
          type="button"
          onClick={openFilterPanel}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 6,
            border: '1px solid #bfc8d6', background: activeFilterCount ? '#eef1f5' : '#fff', fontSize: 14, fontWeight: 500, cursor: 'pointer',
          }}
        >
          <FilterIcon size={15} strokeWidth={2} />
          Filters
          {activeFilterCount > 0 && (
            <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 18, height: 18, padding: '0 4px', borderRadius: 999, background: '#1976d2', color: '#fff', fontSize: 11, fontWeight: 700 }}>
              {activeFilterCount}
            </span>
          )}
        </button>
        <ColumnPicker
          screenKey={FILTER_SCREEN_KEY}
          columns={OPTIONAL_COLUMNS}
          defaultVisible={DEFAULT_VISIBLE_COLUMNS}
          onChange={setVisibleColumns}
          tabColumns={tabColumns}
          systemColumns={SYSTEM_COLUMNS}
        />
      </div>

      <div style={{ margin: '10px 24px 0' }}>
        <SavedFilterTabs
          screenKey={FILTER_SCREEN_KEY}
          tabColumns={tabColumns}
          defaultFilters={DEFAULT_FILTERS}
          currentFilters={filters}
          onApply={applyFilters}
          renderFields={renderDrawerFields}
        />
      </div>

      {filterDraft && (
        <FilterDrawer
          open
          onOpenChange={(next) => { if (!next) closeFilterPanel(); }}
          title="Filters"
          description="Narrow the Document Milestones list."
          footer={(
            <>
              <div>
                <button type="button" onClick={clearFilterPanel} style={{ padding: '8px 12px', border: '1px solid #bfc8d6', color: '#5a6b85', background: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>
                  Clear filters
                </button>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <CancelButton onClick={closeFilterPanel} style={{ padding: '8px 14px' }} />
                <button type="button" onClick={applyFilterPanel} style={{ padding: '8px 14px', border: 'none', color: '#fff', background: '#1976d2', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>Apply</button>
              </div>
            </>
          )}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {renderDrawerFields(filterDraft, setFilterDraftField)}
          </div>
        </FilterDrawer>
      )}

      <div style={{ margin: '18px 24px 0' }}>
        {loading ? (
          <div style={{ padding: '14px', textAlign: 'center', background: '#f6f8fa', border: '1px solid #b3bbc7', borderRadius: 10, fontSize: 14 }}>Loading...</div>
        ) : rows.length === 0 ? (
          <div style={{ padding: '14px', textAlign: 'center', background: '#f6f8fa', border: '1px solid #b3bbc7', borderRadius: 10, fontSize: 14 }}>No matching documents</div>
        ) : rows.map((row) => (
          <Accordion
            key={`${row.document_type}-${row.document_id}`}
            defaultOpen={false}
            title={(
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <span style={{ fontWeight: 600 }}>{row.display_number}</span>
                {showType && <span style={{ fontSize: 12, color: '#666' }}>{DOCUMENT_TYPE_LABELS[row.document_type] || row.document_type}</span>}
                {showCustomer && row.customer_name && <span style={{ fontSize: 13, color: '#5a6b85', fontWeight: 400 }}>{row.customer_name}</span>}
                <span style={{ fontSize: 12, color: '#8a94a6' }}>
                  {row.milestones.length} milestone{row.milestones.length === 1 ? '' : 's'}
                </span>
              </div>
            )}
            menuItems={[
              { key: 'full-history', label: 'Full history', onClick: () => setSelectedRow(row) },
            ]}
          >
            {row.milestones.map((m, idx) => (
              <div
                key={idx}
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, padding: '8px 0', borderBottom: idx < row.milestones.length - 1 ? '1px solid #eee' : 'none' }}
              >
                <div>
                  <div style={{ fontSize: 14 }}>{MILESTONE_LABELS[m.milestone] || m.milestone}</div>
                  {m.comment && <div style={{ fontSize: 13, color: '#666', marginTop: 2 }}>{m.comment}</div>}
                </div>
                <div style={{ fontSize: 13, color: m.occurred_at ? '#333' : '#aab2bd', whiteSpace: 'nowrap' }}>
                  {m.occurred_at ? formatDateTime(m.occurred_at) : 'Date unknown'}
                </div>
              </div>
            ))}
          </Accordion>
        ))}
      </div>

      <Pagination
        pagination={pagination}
        setPage={setPage}
        totalLabel="Documents"
        rightContent={(
          <select className="styled-input" value={limit} onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }} style={{ marginLeft: 8, padding: '2px 4px', border: 'none', background: 'none', fontSize: 14, fontWeight: 600, color: '#333', cursor: 'pointer' }}>
            {PAGE_SIZE_OPTIONS.map((sz) => <option key={sz} value={sz}>{sz} per page</option>)}
          </select>
        )}
      />

      {selectedRow && <MilestoneHistoryOverlay row={selectedRow} onClose={() => setSelectedRow(null)} />}
    </div>
  );
}

export default DocumentMilestonesPage;
